import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  content as contentDefault,
  teaching as teachingDefault,
  nonTeaching as nonTeachingDefault,
} from "../../data/careers";
import { getCareersPage, getJobs, getErpJobs } from "../../services/data.service";
import { mediaUrl } from "../../services/assetUrl";
import Reveal from "../home/Reveal";
import PageBanner from "../../components/PageBanner";
import "../home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .reveal-ed
import "./careersNew.css";
import TeachingForm from "./TeachingForm";
import NonTeachingForm from "./NonTeachingForm";
import { IconGraduation, IconBriefcase, IconHome, IconBell, IconClose } from "./icons";

const typeIcons = { graduation: IconGraduation, briefcase: IconBriefcase };

const Selector = ({ onContinue, content = contentDefault }) => {
  const [picked, setPicked] = useState("");
  const { heading, subtitle, types } = content.selector;
  return (
    <div className="crs-card">
      <div className="crs-card__head">
        <div>
          <p className="crs-card__head-eyebrow">Join NCET</p>
          <h2>{heading}</h2>
        </div>
        <span className="crs-card__head-icon"><IconBriefcase /></span>
      </div>
      <div className="crs-card__body">
        <p className="crs-select-intro section-sub-ed">{subtitle}</p>
        <div className="crs-types">
          {types.map((t) => {
            const Icon = typeIcons[t.icon] || IconBriefcase;
            return (
              <button
                type="button"
                key={t.id}
                className={`crs-type${picked === t.id ? " is-selected" : ""}`}
                onClick={() => setPicked(t.id)}
                aria-pressed={picked === t.id}
              >
                <span className="crs-type__icon"><Icon /></span>
                <h3 className="crs-type__title">{t.title}</h3>
                <p className="crs-type__desc">{t.desc}</p>
                <ul className="crs-type__points">
                  {t.points.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </button>
            );
          })}
        </div>
        <div className="crs-select-actions">
          <button
            type="button"
            className="btn-ed btn-ed--primary"
            disabled={!picked}
            onClick={() => onContinue(picked)}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// One notification row for a live vacancy.
const JobNotice = ({ job }) => {
  const isTeaching = (job.category || "").toLowerCase() === "teaching";
  const Icon = isTeaching ? IconGraduation : IconBriefcase;
  const meta = [job.departments, job.institute].filter(Boolean).join(" · ");
  return (
    <li className={`crs-note${isTeaching ? " is-teaching" : " is-nonteaching"}`}>
      <span className="crs-note__icon"><Icon /></span>
      <div className="crs-note__body">
        <p className="crs-note__pos">{job.position}</p>
        {job.roles && <p className="crs-note__role">{job.roles}</p>}
        {meta && <p className="crs-note__meta">{meta}</p>}
      </div>
      <span className="crs-note__tag">{isTeaching ? "Teaching" : "Non-Teaching"}</span>
    </li>
  );
};

// "Current Openings" — a notification bell with an unread-count badge. Clicking it
// opens a dropdown panel listing the live vacancies. Closes on outside-click / Esc.
const OpeningsBell = ({ jobs, source }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const count = jobs ? jobs.length : 0;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (count === 0) return null;

  return (
    <div className={`crs-bellwrap${open ? " is-open" : ""}`} ref={wrapRef}>
      <button
        type="button"
        className="crs-bell"
        aria-label={`${count} current job openings`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconBell />
        <span className="crs-bell__badge">{count}</span>
      </button>

      {open && (
        <div className="crs-bellpanel" role="dialog" aria-label="Current openings">
          <span className="crs-bellpanel__caret" />
          <div className="crs-bellpanel__head">
            <div>
              <p className="crs-bellpanel__eyebrow">Now Hiring</p>
              <h3 className="crs-bellpanel__title">Current Openings</h3>
            </div>
            <button
              type="button"
              className="crs-bellpanel__close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <IconClose />
            </button>
          </div>
          <ul className="crs-bellpanel__list">
            {jobs.map((job, i) => (
              <JobNotice key={job._id || `${job.position}-${i}`} job={job} />
            ))}
          </ul>
          <div className="crs-bellpanel__foot">
            {count} position{count === 1 ? "" : "s"} open · pick a type below to apply
            {source === "LOCAL" ? " · saved copy (ERP offline)" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

const CareersMain = () => {
  const [rectype, setRectype] = useState("");
  const reset = () => setRectype("");

  // Backend-driven content with bundled JSON as the instant fallback (same
  // pattern as Home / Placements). Forms receive the resolved slices as props.
  const [content, setContent] = useState(contentDefault);
  const [teaching, setTeaching] = useState(teachingDefault);
  const [nonTeaching, setNonTeaching] = useState(nonTeachingDefault);
  // Current Openings for the bell. Starts empty so a down backend can never
  // flash the stale bundled jobs.json — no live data, no bell.
  const [jobs, setJobs] = useState([]);
  const [source, setSource] = useState("ERP");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getCareersPage();
        const d = res?.data?.data; // SERVICE + wrapped doc → res.data.data
        if (active && d) {
          if (d.content) setContent(d.content);
          if (d.teaching) setTeaching(d.teaching);
          if (d.nonTeaching) setNonTeaching(d.nonTeaching);
        }
      } catch (e) {
        /* keep bundled fallback on 404 / error */
      }
    })();
    // Live openings for the bell. The backend prefers the ERP and falls back
    // to its local Job collection while the ERP is unreachable (response
    // `source` tells which). If the request fails entirely the bell simply
    // stays hidden — stale bundled jobs are never shown.
    // NOTE: http.common.js unwraps axios responses (interceptor returns
    // response.data), so these helpers resolve DIRECTLY to the body:
    //   getErpJobs() → { success, source, count, data: [...] }
    //   getJobs()    → { success, count, data: [...] }
    const fetchOpenings = async () => {
      try {
        const body = await getErpJobs();
        const list = body?.data; // { success, source, count, data: [...] }
        if (active && Array.isArray(list)) {
          setJobs(list);
          setSource(body?.source === "LOCAL" ? "LOCAL" : "ERP");
        }
      } catch (e) {
        try {
          const body = await getJobs();
          const list = body?.data; // { success, count, data: [...] }
          if (active && Array.isArray(list)) {
            setJobs(list);
            setSource("LOCAL");
          }
        } catch (e2) {
          /* backend unreachable — leave the bell hidden rather than stale */
        }
      }
    };

    fetchOpenings();
    // Re-sync when the tab becomes visible again so ERP publishes/closures
    // reflect without a manual reload.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchOpenings();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const titleParts = content.title.trim().split(" ");
  const titleLast = titleParts.pop();

  return (
    <div className="react-wrapper">
      <div className="react-wrapper-inner">
       <div className="crs-page">
        {/* notification bell — floats over the hero top-right, clear of the
            fixed Apply/Enquire rail; its dropdown panel opens downward */}
        <div className="crs-bell-layer">
          <OpeningsBell jobs={jobs} source={source} />
        </div>

        {/* Shared PageBanner (hero-consistency); replaces the old custom crs-banner */}
        <PageBanner
          title={content.title}
          eyebrow={content.eyebrow}
          subtitle={content.subtitle}
          image={content.bannerImage ? mediaUrl(content.bannerImage) : undefined}
        />

        <section className="crs-section">
          <div className="container">
            <div className="crs-stage">
              <Reveal>
                {rectype === "Teaching" ? (
                  <TeachingForm onExit={reset} content={content} teaching={teaching} />
                ) : rectype === "Non Teaching" ? (
                  <NonTeachingForm onExit={reset} content={content} nonTeaching={nonTeaching} />
                ) : (
                  <Selector onContinue={setRectype} content={content} />
                )}
              </Reveal>
            </div>
          </div>
        </section>
       </div>
      </div>
    </div>
  );
};

export default CareersMain;
